import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Activity, AlertTriangle, ArrowUpRight, Ban, BrainCircuit, CheckCircle2,
    ChevronDown, ChevronRight, Clock, Coins, Cpu, Gauge, Layers, Loader2,
    MailPlus, PackagePlus, PenLine, Play, RefreshCw, RotateCcw, Send,
    Sparkles, Users, Wrench,
} from "lucide-react";
import { toast } from "sonner";

import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/data/v12_user_nav";


const PRIORITY_STYLE = {
    high: "text-[var(--color-alert)] bg-[var(--color-alert)]/10 border-[var(--color-alert)]/40",
    medium: "text-neutral-200 bg-neutral-800 border-neutral-700",
    low: "text-text-secondary bg-white/5 border-white/10",
};

const RISK_STYLE = {
    high: "text-neutral-100",
    medium: "text-neutral-300",
    low: "text-[var(--color-success)]",
};

const ENGINE_META = {
    email_generator: {
        icon: PenLine, order: 1,
        title: "P0.1 · AutoEmailGenerator",
        subtitle: "1 LLM call · 7 canonical lifecycle templates",
    },
    segmentation_engine: {
        icon: Users, order: 2,
        title: "P0.2 · AutoSegmentationEngine",
        subtitle: "Rules · 5 deterministic segments",
    },
    personalization_engine: {
        icon: Wrench, order: 3,
        title: "P0.3 · AutoPersonalizationEngine",
        subtitle: "Rules · placeholder merge helper (audit sample)",
    },
    upgrade_engine: {
        icon: Sparkles, order: 4,
        title: "P0.4 · AutoUpgradeEngine",
        subtitle: "1 LLM call · wraps dynamic_offers pending",
    },
    credits_engine: {
        icon: Coins, order: 5,
        title: "P0.5 · AutoCreditsEngine",
        subtitle: "1 LLM call · wraps etf_bundle_drafts pending",
    },
    retention_engine: {
        icon: RotateCcw, order: 6,
        title: "P0.6 · AutoRetentionEngine",
        subtitle: "1 LLM call · wraps recycling/mini-landings/modules",
    },
};


function KpiCard({ icon: Icon, label, value, tone = "primary", testid }) {
    const toneClass = {
        primary: "text-[var(--color-primary)]",
        success: "text-[var(--color-success)]",
        alert: "text-[var(--color-alert)]",
        warning: "text-neutral-200",
        secondary: "text-[var(--color-secondary)]",
    }[tone];
    return (
        <div
            data-testid={testid}
            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4"
        >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary mb-2">
                <Icon className={`h-3 w-3 ${toneClass}`} /> {label}
            </div>
            <div className={`text-2xl font-semibold tabular-nums ${toneClass}`}>
                {value}
            </div>
        </div>
    );
}


function PriorityChip({ level, testid }) {
    const cls = PRIORITY_STYLE[String(level || "low").toLowerCase()] || PRIORITY_STYLE.low;
    return (
        <span data-testid={testid}
            className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${cls}`}>
            {level || "low"}
        </span>
    );
}


function SuggestionCard({ suggestion, onChanged }) {
    const [busy, setBusy] = useState(false);
    const status = suggestion.status || "pending";
    const isPending = status === "pending";
    const [expanded, setExpanded] = useState(false);

    const act = async (kind) => {
        setBusy(true);
        try {
            const path = kind === "ack"
                ? `/email-engine/acknowledge/${encodeURIComponent(suggestion.id)}`
                : `/email-engine/dismiss/${encodeURIComponent(suggestion.id)}`;
            const body = kind === "dismiss"
                ? { reason: prompt("Dismiss reason (optional):") || "" }
                : {};
            await apiFetch(path, { method: "POST", body: JSON.stringify(body) });
            toast.success(kind === "ack" ? "Acknowledged" : "Dismissed");
            onChanged?.();
        } catch (e) {
            toast.error(e.message || "Action failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <li
            data-testid={`email-suggestion-${suggestion.id}`}
            className="rounded-xl border border-[var(--color-border)] bg-[var(--color-panel-ai)] px-4 py-3"
        >
            <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                        <PriorityChip level={suggestion.priority} />
                        {suggestion.risk && (
                            <span className={`text-[10px] uppercase tracking-widest ${RISK_STYLE[String(suggestion.risk).toLowerCase()] || "text-text-secondary"}`}>
                                risk · {suggestion.risk}
                            </span>
                        )}
                        {suggestion.wrapped_source && (
                            <span className="text-[10px] uppercase tracking-widest text-[var(--color-secondary)] flex items-center gap-1">
                                <Layers className="h-3 w-3" /> wraps · {suggestion.wrapped_source}
                            </span>
                        )}
                        {suggestion.ai_generated && (
                            <span className="text-[10px] uppercase tracking-widest text-[var(--color-primary)] flex items-center gap-1">
                                <Sparkles className="h-3 w-3" /> ai
                            </span>
                        )}
                        {status !== "pending" && (
                            <span className={`text-[10px] uppercase tracking-widest ${status === "acknowledged" ? "text-[var(--color-success)]" : "text-text-secondary"}`}>
                                · {status}
                            </span>
                        )}
                    </div>
                    <div className="text-sm text-text-main font-medium">
                        {suggestion.subject || "(no subject)"}
                    </div>
                    {suggestion.reasoning && (
                        <p className="text-xs text-text-secondary italic mt-1">
                            {suggestion.reasoning}
                        </p>
                    )}
                    {suggestion.impact && (
                        <p className="text-[11px] text-text-secondary mt-1">
                            → {suggestion.impact}
                        </p>
                    )}
                    {(suggestion.target_user_count > 0 || suggestion.psychological_lever
                        || suggestion.angle || suggestion.reactivation_angle) && (
                        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-text-secondary mt-2 uppercase tracking-widest">
                            {suggestion.target_user_count > 0 && (
                                <span>target · {suggestion.target_user_count} users</span>
                            )}
                            {suggestion.psychological_lever && (
                                <span>lever · {suggestion.psychological_lever}</span>
                            )}
                            {suggestion.angle && (
                                <span>angle · {suggestion.angle}</span>
                            )}
                            {suggestion.reactivation_angle && (
                                <span>reactivation · {suggestion.reactivation_angle}</span>
                            )}
                        </div>
                    )}
                    {suggestion.body_markdown && (
                        <button
                            onClick={() => setExpanded((v) => !v)}
                            data-testid={`email-body-toggle-${suggestion.id}`}
                            className="text-[10px] uppercase tracking-widest text-[var(--color-primary)] mt-2"
                        >
                            {expanded ? "Hide body" : "Show body"}
                        </button>
                    )}
                    {expanded && suggestion.body_markdown && (
                        <pre className="mt-2 whitespace-pre-wrap text-xs text-text-secondary bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg p-3">
                            {suggestion.body_markdown}
                        </pre>
                    )}
                </div>
                {isPending && (
                    <div className="flex flex-col gap-1 shrink-0">
                        <button
                            data-testid={`email-ack-${suggestion.id}`}
                            disabled={busy}
                            onClick={() => act("ack")}
                            className="text-[10px] rounded-full border border-[var(--color-primary)]/40 text-[var(--color-primary)] px-3 py-1 hover:bg-[var(--color-primary)]/10 disabled:opacity-50 flex items-center gap-1"
                        >
                            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                            Ack
                        </button>
                        <button
                            data-testid={`email-dismiss-${suggestion.id}`}
                            disabled={busy}
                            onClick={() => act("dismiss")}
                            className="text-[10px] rounded-full border border-[var(--color-border)] text-text-secondary px-3 py-1 hover:text-text-main disabled:opacity-50 flex items-center gap-1"
                        >
                            <Ban className="h-3 w-3" />
                            Dismiss
                        </button>
                    </div>
                )}
            </div>
        </li>
    );
}


function BlockShell({ engineKey, engine, defaultOpen, onChanged }) {
    const [open, setOpen] = useState(!!defaultOpen);
    const meta = ENGINE_META[engineKey];
    if (!meta) return null;
    const Icon = meta.icon;
    const suggestions = engine?.suggestions || [];
    const skipped = !!engine?.skipped;
    const priority = engine?.priority || "low";
    const impact = engine?.impact || "—";
    const testid = `email-block-${meta.order}`;

    return (
        <section
            data-testid={testid}
            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden"
        >
            <button
                onClick={() => setOpen((v) => !v)}
                data-testid={`${testid}-toggle`}
                className="w-full flex items-center justify-between p-5 md:p-6 text-left hover:bg-[var(--color-panel-ai)]/40 transition-colors"
            >
                <div className="flex items-start gap-4 min-w-0">
                    <div className="h-10 w-10 rounded-xl bg-[var(--color-primary)]/10 border border-[var(--color-primary)]/30 grid place-items-center shrink-0">
                        <Icon className="h-5 w-5 text-[var(--color-primary)]" />
                    </div>
                    <div className="min-w-0">
                        <h3 className="text-base md:text-lg font-semibold text-text-main truncate">
                            {meta.title}
                        </h3>
                        <p className="text-xs text-text-secondary mt-0.5">{meta.subtitle}</p>
                    </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    {skipped ? (
                        <span className="text-[10px] uppercase tracking-widest text-text-secondary">
                            skipped
                        </span>
                    ) : (
                        <>
                            <PriorityChip level={priority} />
                            <span className="text-xs text-text-secondary tabular-nums">
                                {suggestions.length} suggestions
                            </span>
                        </>
                    )}
                    {open ? <ChevronDown className="h-4 w-4 text-text-secondary" /> : <ChevronRight className="h-4 w-4 text-text-secondary" />}
                </div>
            </button>
            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.18 }}
                        className="overflow-hidden"
                    >
                        <div className="border-t border-[var(--color-border)] p-5 md:p-6 space-y-3">
                            {skipped ? (
                                <p className="text-xs text-text-secondary italic">
                                    Engine skipped — {engine?.reason || "disabled via feature flag"}.
                                </p>
                            ) : (
                                <>
                                    {engine?.reasoning && (
                                        <p className="text-xs text-text-secondary italic">
                                            {engine.reasoning}
                                        </p>
                                    )}
                                    <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary">
                                        <span>impact · {impact}</span>
                                        {engine?.risk && <span>· risk · {engine.risk}</span>}
                                        {engine?.duration_ms != null && (
                                            <span>· {engine.duration_ms}ms</span>
                                        )}
                                        {engine?.llm_used && <span>· llm ✓</span>}
                                    </div>
                                    {suggestions.length === 0 ? (
                                        <p className="text-xs text-text-secondary">
                                            No suggestions from this engine.
                                        </p>
                                    ) : (
                                        <ul className="space-y-2">
                                            {suggestions.map((s) => (
                                                <SuggestionCard
                                                    key={s.id}
                                                    suggestion={s}
                                                    onChanged={onChanged}
                                                />
                                            ))}
                                        </ul>
                                    )}
                                </>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}


function SegmentsPanel({ segments }) {
    return (
        <div
            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4"
            data-testid="email-segments-panel"
        >
            <div className="text-[10px] uppercase tracking-widest text-text-secondary mb-3">
                Segments (P0.2)
            </div>
            <ul className="space-y-2">
                {segments.length === 0 && (
                    <li className="text-xs text-text-secondary italic">
                        Run the engine to materialise segments.
                    </li>
                )}
                {segments.map((s) => (
                    <li
                        key={s.id}
                        data-testid={`email-segment-${s.name}`}
                        className="flex items-center justify-between rounded-lg bg-[var(--color-panel-ai)] px-3 py-2"
                    >
                        <span className="text-xs text-text-main">{s.name}</span>
                        <span className="text-xs text-[var(--color-primary)] tabular-nums">
                            {s.user_count}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}


export default function EmailEngineDashboard() {
    const { user: me } = useAuth();
    const [reports, setReports] = useState([]);
    const [currentId, setCurrentId] = useState(null);
    const [current, setCurrent] = useState(null);
    const [segments, setSegments] = useState([]);
    const [totals, setTotals] = useState({});
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);

    const loadReports = useCallback(async () => {
        try {
            const r = await apiFetch("/email-engine/reports?limit=10");
            setReports(r.items || []);
            if (!currentId && (r.items || []).length > 0) {
                setCurrentId(r.items[0].id);
            }
        } catch (e) {
            if (e.status === 503) {
                toast.error("Email Engine is disabled — flip EMAIL_ENGINE_ENABLED back on in Admin → System");
            } else {
                toast.error(e.message || "Failed to list reports");
            }
        }
    }, [currentId]);

    const loadCurrent = useCallback(async (id) => {
        if (!id) { setCurrent(null); return; }
        try {
            const r = await apiFetch(`/email-engine/run/${encodeURIComponent(id)}`);
            setCurrent(r);
        } catch (e) {
            toast.error(e.message || "Failed to load report");
        }
    }, []);

    const loadSegments = useCallback(async () => {
        try {
            const r = await apiFetch("/email-engine/segments");
            setSegments(r.items || []);
        } catch (e) {
            if (e.status !== 503) {
                console.error("segments err", e);
            }
        }
    }, []);

    const loadTotals = useCallback(async () => {
        try {
            const r = await apiFetch("/email-engine/totals");
            setTotals(r || {});
        } catch (e) {
            if (e.status !== 503) console.error("totals err", e);
        }
    }, []);

    useEffect(() => {
        (async () => {
            setLoading(true);
            await Promise.all([loadReports(), loadSegments(), loadTotals()]);
            setLoading(false);
        })();
    }, [loadReports, loadSegments, loadTotals]);

    useEffect(() => { loadCurrent(currentId); }, [currentId, loadCurrent]);

    const runNow = async () => {
        setRunning(true);
        try {
            const r = await apiFetch("/email-engine/run", { method: "POST", body: JSON.stringify({}) });
            toast.success(`Run complete · ${r?.totals?.suggestions_persisted || 0} suggestions · ${r?.llm_calls_count || 0} LLM calls`);
            setCurrentId(r.id);
            await Promise.all([loadReports(), loadSegments(), loadTotals()]);
        } catch (e) {
            if (e.status === 429) {
                toast.error("Daily quota reached (5/day). Try again tomorrow.");
            } else if (e.status === 503) {
                toast.error("Email Engine is disabled.");
            } else {
                toast.error(e.message || "Run failed");
            }
        } finally {
            setRunning(false);
        }
    };

    const errors = current?.errors || {};
    const errorCount = Object.keys(errors).length;

    const kpis = useMemo(() => ({
        templates: totals.templates || 0,
        variants: totals.variants || 0,
        segments: totals.segments || 0,
        pending: totals.pending || 0,
        acknowledged: totals.acknowledged || 0,
    }), [totals]);

    const orderedEngineKeys = [
        "email_generator", "segmentation_engine", "personalization_engine",
        "upgrade_engine", "credits_engine", "retention_engine",
    ];

    if (!isAdminRole(me?.role)) {
        return (
            <div
                data-testid="email-engine-forbidden"
                className="min-h-screen bg-[var(--color-bg)] text-text-main flex items-center justify-center"
            >
                <h1 className="text-2xl">Admin only</h1>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex">
            <Sidebar />
            <div className="flex-1 flex flex-col min-w-0">
                <Header />
                <main
                    className="flex-1 p-6 md:p-8 max-w-6xl mx-auto w-full"
                    data-testid="email-engine-dashboard"
                >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6 gap-3">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-[var(--color-secondary)] mb-1">
                                Email Engine · V10
                            </div>
                            <h1 className="text-3xl md:text-4xl font-semibold text-text-main">
                                Meta-intelligence email advisor
                            </h1>
                            <p className="text-sm text-text-secondary mt-1 max-w-2xl">
                                Wraps Phase-7B drip, dynamic offers, ETF drafts,
                                content recycling & mini-landings into unified
                                email suggestions. Human-in-the-loop: nothing is
                                ever sent — approve marks as acknowledged only.
                            </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <a
                                href="/admin/governance"
                                data-testid="email-back-governance"
                                className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-4 py-2 flex items-center gap-1"
                            >
                                Governance <ArrowUpRight className="h-3 w-3" />
                            </a>
                            <button
                                onClick={() => { loadReports(); loadSegments(); loadTotals(); loadCurrent(currentId); }}
                                data-testid="email-refresh"
                                className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-4 py-2 flex items-center gap-2"
                            >
                                <RefreshCw className="h-3 w-3" /> Refresh
                            </button>
                            <button
                                onClick={runNow}
                                disabled={running}
                                data-testid="email-run-now"
                                className="text-xs rounded-full bg-white hover:bg-[#EDEDED] text-black px-5 py-2 font-medium flex items-center gap-2 disabled:opacity-50"
                            >
                                {running ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
                                Run Email Engine now
                            </button>
                        </div>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5 mb-6">
                        <KpiCard icon={PenLine} label="Templates" value={kpis.templates}
                                  tone="primary" testid="email-kpi-templates" />
                        <KpiCard icon={PackagePlus} label="Variants" value={kpis.variants}
                                  tone="secondary" testid="email-kpi-variants" />
                        <KpiCard icon={Users} label="Segments" value={kpis.segments}
                                  tone="primary" testid="email-kpi-segments" />
                        <KpiCard icon={MailPlus} label="Pending suggestions" value={kpis.pending}
                                  tone="warning" testid="email-kpi-pending" />
                        <KpiCard icon={CheckCircle2} label="Acknowledged" value={kpis.acknowledged}
                                  tone="success" testid="email-kpi-acknowledged" />
                    </div>

                    <div className="grid gap-6 lg:grid-cols-[240px_1fr]">
                        <aside className="lg:sticky lg:top-6 lg:self-start space-y-4">
                            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
                                <div className="text-[10px] uppercase tracking-widest text-text-secondary mb-3">
                                    Report history
                                </div>
                                {loading ? (
                                    <div className="flex justify-center py-4">
                                        <Loader2 className="h-4 w-4 animate-spin text-[var(--color-primary)]" />
                                    </div>
                                ) : reports.length === 0 ? (
                                    <p className="text-xs text-text-secondary">
                                        Click <span className="text-text-main">Run Email Engine now</span> to start.
                                    </p>
                                ) : (
                                    <ul className="space-y-1 max-h-72 overflow-y-auto pr-1">
                                        {reports.map((r) => (
                                            <li key={r.id}>
                                                <button
                                                    onClick={() => setCurrentId(r.id)}
                                                    data-testid={`email-report-item-${r.id}`}
                                                    className={`w-full text-left rounded-lg px-3 py-2 text-xs transition-colors ${
                                                        r.id === currentId
                                                            ? "bg-[var(--color-panel-ai)] text-text-main"
                                                            : "text-text-secondary hover:bg-[var(--color-panel-ai)]/60 hover:text-text-main"
                                                    }`}
                                                >
                                                    <div className="tabular-nums">{(r.generated_at || "").slice(0, 16).replace("T", " ")}</div>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <span className="text-[10px] uppercase tracking-widest">{r.triggered_by}</span>
                                                        <span className="text-[10px] text-[var(--color-primary)]">
                                                            · {r.totals?.suggestions_persisted || 0}
                                                        </span>
                                                        <span className="text-[10px] text-[var(--color-secondary)]">
                                                            · {r.llm_calls_count || 0} llm
                                                        </span>
                                                        {r.status !== "completed" && (
                                                            <span className="text-[10px] text-[var(--color-alert)]">· {r.status}</span>
                                                        )}
                                                    </div>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <SegmentsPanel segments={segments} />
                        </aside>

                        <div className="space-y-4">
                            {!current ? (
                                <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-12 text-center">
                                    <MailPlus className="h-8 w-8 text-[var(--color-primary)] mx-auto mb-3" />
                                    <p className="text-sm text-text-secondary">
                                        Run the Email Engine to generate suggestions.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5"
                                        data-testid="email-run-header">
                                        <div className="flex flex-wrap items-center gap-4 text-xs text-text-secondary">
                                            <span className="flex items-center gap-1">
                                                <Clock className="h-3 w-3" />
                                                {(current.generated_at || "").slice(0, 19).replace("T", " ")} UTC
                                            </span>
                                            <span>· duration {current.duration_ms}ms</span>
                                            <span>· <span className="text-text-main">{current.llm_calls_count || 0}</span> LLM calls</span>
                                            <span>· trigger <span className="text-text-main">{current.triggered_by}</span></span>
                                            <span>· top priority <PriorityChip level={current.totals?.top_priority} /></span>
                                        </div>
                                        {errorCount > 0 && (
                                            <div className="mt-3 rounded-lg border border-[var(--color-alert)]/40 bg-[var(--color-alert)]/10 px-3 py-2 text-xs text-[var(--color-alert)] flex items-start gap-2">
                                                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                                                <div>
                                                    <div className="font-medium">Partial run — {errorCount} engine(s) failed</div>
                                                    <div className="opacity-80 truncate">
                                                        {Object.entries(errors).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {orderedEngineKeys.map((k, idx) => (
                                        <BlockShell
                                            key={k}
                                            engineKey={k}
                                            engine={current.engines?.[k]}
                                            defaultOpen={idx === 0}
                                            onChanged={() => { loadCurrent(current.id); loadTotals(); }}
                                        />
                                    ))}
                                </>
                            )}
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
}
